import { useDispatch } from "react-redux";
import { useCallback } from "react";
import { getFilterDropdowns } from "../API/getFilterDropdowns";
import { savePackageOptionMaster, IFilterDropdown } from "../AppState/overagesFilterFormActions";

/**
 * Custom hook for managing package master data
 * 
 * This hook encapsulates the common package code loading logic used across multiple components.
 * It fetches package options from the getFilterDropdowns API and saves them to Redux state.
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { loadPackageOptions } = usePackageMaster();
 *   const packageOptions = useSelector((state: AppState) => state.packageOptionMasterData);
 * 
 *   useEffect(() => {
 *     if (!packageOptions?.length) {
 *       loadPackageOptions();
 *     }
 *   }, [loadPackageOptions, packageOptions?.length]);
 * 
 *   // Component JSX...
 * }
 * ```
 * 
 * @returns {Object} Hook methods
 * @returns {Function} loadPackageOptions - Function to load and initialize package master data
 */
export const usePackageMaster = () => {
  const dispatch = useDispatch();

  /**
   * Fetches package options and saves them to Redux state
   * 
   * This function:
   * 1. Calls the getFilterDropdowns API with dataType "packageCode"
   * 2. Dispatches the results to Redux using savePackageOptionMaster
   * 3. Handles errors gracefully with console logging in development
   */
  const loadPackageOptions = useCallback(() => {
    const params: { dataType: string } = {
      dataType: "packageCode",
    };

    getFilterDropdowns(params)
      .then((results: IFilterDropdown[]) => {
        dispatch(savePackageOptionMaster(results));
      })
      .catch((error: any) => {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading package options:', error);
        }
      });
  }, [dispatch]);

  return {
    loadPackageOptions
  };
};
